import React, { Component } from 'react';
import { SellingAllowed, SellingAccessContext } from '../../../context/sellingAccessContext';
import { array, arrayOf, bool, func, number, object, shape, string } from 'prop-types';
import pickBy from 'lodash/pickBy';
import classNames from 'classnames';
import { GoBell } from 'react-icons/go';
import appSettings from '../../../config/settings';
import { useConfiguration } from '../../../context/configurationContext';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { IoIosContact } from 'react-icons/io';
import { IoMdMenu } from 'react-icons/io';
import { FaRegHeart } from 'react-icons/fa';
import { FormattedMessage, intlShape, useIntl } from '../../../util/reactIntl';
import { isMainSearchTypeKeywords, isOriginInUse } from '../../../util/search';
import { withViewport } from '../../../util/uiHelpers';
import { parse, stringify } from '../../../util/urlHelpers';
import { createResourceLocatorString, pathByRouteName } from '../../../util/routes';
import { propTypes } from '../../../util/types';
import {
  Avatar,
  Button,
  InlineTextButton,
  LimitedAccessBanner,
  LinkedLogo,
  MegaMenu,
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  Modal,
  ModalMissingInformation,
  NamedLink,
  OfferBar,
} from '../../../components';

import MenuIcon from './MenuIcon';
import SearchIcon from './SearchIcon';
import TopbarSearchForm from './TopbarSearchForm/TopbarSearchForm';
import TopbarMobileMenu from './TopbarMobileMenu/TopbarMobileMenu';
import TopbarDesktop from './TopbarDesktop/TopbarDesktop';
import image from '../../../assets/contact.png';
import css from './Topbar.module.css';
import { ACCOUNT_SETTINGS_PAGES } from '../../../routing/routeConfiguration';

const MAX_MOBILE_SCREEN_WIDTH = 1024;

const redirectToURLWithModalState = (props, modalStateParam) => {
  const { history, location } = props;
  const { pathname, search, state } = location;
  const searchString = `?${stringify({ [modalStateParam]: 'open', ...parse(search) })}`;
  history.push(`${pathname}${searchString}`, state);
};

const redirectToURLWithoutModalState = (props, modalStateParam) => {
  const { history, location } = props;
  const { pathname, search, state } = location;
  const queryParams = pickBy(parse(search), (v, k) => {
    return k !== modalStateParam;
  });
  const stringified = stringify(queryParams);
  const searchString = stringified ? `?${stringified}` : '';
  history.push(`${pathname}${searchString}`, state);
};

const GenericError = props => {
  const { show } = props;
  const classes = classNames(css.genericError, {
    [css.genericErrorVisible]: show,
  });
  return (
    <div className={classes}>
      <div className={css.genericErrorContent}>
        <p className={css.genericErrorText}>
          <FormattedMessage id="Topbar.genericError" />
        </p>
      </div>
    </div>
  );
};

GenericError.propTypes = {
  show: bool.isRequired,
};

class TopbarComponent extends Component {
  static contextType = SellingAccessContext;
  constructor(props) {
    super(props);
    this.handleMobileMenuOpen = this.handleMobileMenuOpen.bind(this);
    this.handleMobileMenuClose = this.handleMobileMenuClose.bind(this);
    this.handleMobileSearchOpen = this.handleMobileSearchOpen.bind(this);
    this.handleMobileSearchClose = this.handleMobileSearchClose.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleLogout = this.handleLogout.bind(this);
  }

  handleMobileMenuOpen() {
    redirectToURLWithModalState(this.props, 'mobilemenu');
  }

  handleMobileMenuClose() {
    redirectToURLWithoutModalState(this.props, 'mobilemenu');
  }

  handleMobileSearchOpen() {
    redirectToURLWithModalState(this.props, 'mobilesearch');
  }

  handleMobileSearchClose() {
    redirectToURLWithoutModalState(this.props, 'mobilesearch');
  }

  handleSubmit(values) {
    const { history, config, routeConfiguration } = this.props;

    const topbarSearchParams = () => {
      if (isMainSearchTypeKeywords(config)) {
        const keywords = values?.keywords?.trim();
        return {
          keywords,
          fromSearch: 'true',
          pub_department: 'women',
          sort: keywords ? 'relevance' : 'createdAt',
        };
      }
      // topbar search defaults to 'location' search
      const { search, selectedPlace } = values?.location;
      const { origin, bounds } = selectedPlace;
      const originMaybe = isOriginInUse(config) ? { origin } : {};

      return {
        ...originMaybe,
        address: search,
        bounds,
        pub_department: 'women',
        sort: 'createdAt',
      };
    };
    const searchParams = topbarSearchParams();
    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, searchParams));
  }

  handleLogout() {
    const { onLogout, history, routeConfiguration } = this.props;
    onLogout().then(() => {
      const path = pathByRouteName('LandingPage', routeConfiguration);

      // In production we ensure that data is really lost,
      // but in development mode we use stored values for debugging
      if (appSettings.dev) {
        history.push(path);
      } else if (typeof window !== 'undefined') {
        window.location = path;
      }

      console.log('logged out'); // eslint-disable-line
    });
  }

  render() {
    const {
      className,
      rootClassName,
      desktopClassName,
      mobileRootClassName,
      mobileClassName,
      isAuthenticated,
      authScopes,
      authInProgress,
      currentUser,
      currentUserHasListings,
      currentUserHasOrders,
      currentPage,
      notificationCount,
      viewport,
      intl,
      location,
      onManageDisableScrolling,
      onResendVerificationEmail,
      sendVerificationEmailInProgress,
      sendVerificationEmailError,
      showGenericError,
      config,
      emailVerifyPopupOpen,
      onHandleClosePopUp,
      onHandleShowPopUp,
      showFilterInMobile,
      history,
      routeConfiguration,
      currentSearchParams,
    } = this.props;
    const pathname = location.pathname;
    const { mobilemenu, mobilesearch, keywords, address, origin, bounds } = parse(location.search, {
      latlng: ['origin'],
      latlngBounds: ['bounds'],
    });

    const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;
    const isMobileFilterOpen = showFilterInMobile === 'open';
    const isMobileLayout = viewport.width < MAX_MOBILE_SCREEN_WIDTH;
    const isMobileMenuOpen = isMobileLayout && mobilemenu === 'open';
    const isMobileSearchOpen = isMobileLayout && mobilesearch === 'open';
    const currentPageClass = page => {
      const isAccountSettingsPage =
        page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
      return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
    };
    const { attributes } = currentUser || {};
    const { emailVerified } = attributes || {};
    const showMissingInformationReminder = isAuthenticated && !emailVerified;
    const mobileMenu = (
      <TopbarMobileMenu
        isAuthenticated={isAuthenticated}
        currentUserHasListings={currentUserHasListings}
        currentUser={currentUser}
        onLogout={this.handleLogout}
        notificationCount={notificationCount}
        currentPage={currentPage}
        intl={intl}
        showMissingInformationReminder={showMissingInformationReminder}
        onHandleShowPopUp={onHandleShowPopUp}
      />
    );

    const isSearchPage = currentPage === 'SearchPage';
    const topbarSearcInitialValues = () => {
      if (isMainSearchTypeKeywords(config)) {
        return { keywords: isSearchPage ? '' : keywords };
      }

      // Only render current search if full place object is available in the URL params
      const locationFieldsPresent = isOriginInUse(config)
        ? address && origin && bounds
        : address && bounds;
      return {
        location: locationFieldsPresent
          ? {
              search: address,
              selectedPlace: { address, origin, bounds },
            }
          : null,
      };
    };
    const initialSearchFormValues = topbarSearcInitialValues();

    const classes = classNames(rootClassName || css.root, className);
    const profileMenu = isAuthenticated ? (
      <Menu>
        <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
          <img src={image} className={css.avatar} />
        </MenuLabel>
        <MenuContent className={css.profileMenuContent} showArrow={false}>
          {this.context && (
            <MenuItem key="ManageListingsPage">
              <NamedLink
                className={classNames(css.yourListingsLink, currentPageClass('ManageListingsPage'))}
                name="ManageListingsPage"
              >
                <span className={css.menuItemBorder} />
                <FormattedMessage id="TopbarDesktop.yourListingsLink" />
              </NamedLink>
            </MenuItem>
          )}
          <MenuItem key="InboxBaseOrder">
            <NamedLink
              className={classNames(css.yourListingsLink, currentPageClass('InboxBaseOrder'))}
              params={{ tab: 'orders' }}
              name="InboxBaseOrder"
            >
              {' '}
              <span className={css.menuItemBorder} />
              {intl.formatMessage({ id: 'TopbarDesktop.TopbarDesktop.myOrders' })}
            </NamedLink>
          </MenuItem>
          {this.context && (
            <MenuItem key="InboxBasePage">
              <NamedLink
                className={classNames(css.yourListingsLink, currentPageClass('InboxBasePage'))}
                params={{ tab: 'sales' }}
                name="InboxBasePage"
              >
                {' '}
                <span className={css.menuItemBorder} />
                {intl.formatMessage({ id: 'TopbarDesktop.TopbarDesktop.mySales' })}
              </NamedLink>
            </MenuItem>
          )}
          <MenuItem key="ProfileSettingsPage">
            <NamedLink
              className={classNames(
                css.profileSettingsLink,
                currentPageClass('ProfileSettingsPage')
              )}
              name="ProfileSettingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
            </NamedLink>
          </MenuItem>
          <MenuItem key="AccountSettingsPage">
            <NamedLink
              className={classNames(css.yourListingsLink, currentPageClass('AccountSettingsPage'))}
              name="AccountSettingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
            </NamedLink>
          </MenuItem>

          <MenuItem key="logout">
            <InlineTextButton rootClassName={css.logoutButton} onClick={this.handleLogout}>
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.logout" />
            </InlineTextButton>
          </MenuItem>
        </MenuContent>
      </Menu>
    ) : null;
    return (
      <div className={classes}>
        <LimitedAccessBanner
          isAuthenticated={isAuthenticated}
          authScopes={authScopes}
          currentUser={currentUser}
          onLogout={this.handleLogout}
          currentPage={currentPage}
        />
        <div className={classNames(mobileRootClassName || css.container, mobileClassName)}>
          <div className={css.leftContainer}>
            <Button
              rootClassName={css.menu}
              onClick={this.handleMobileMenuOpen}
              title={intl.formatMessage({ id: 'Topbar.menuIcon' })}
            >
              <IoMdMenu className={css.menuIcon} />
            </Button>
            <LinkedLogo layout={'mobile'} alt={intl.formatMessage({ id: 'Topbar.logoIcon' })} />
          </div>
          <div className={css.right}>
            <SellingAllowed>
              {showMissingInformationReminder ? (
                <div className={css.createListingButton} onClick={() => onHandleShowPopUp()}>
                  <FormattedMessage id="Topbar.Topbar.sellLabel" />
                </div>
              ) : (
                <NamedLink name="NewListingPage" className={css.createListingButton}>
                  <FormattedMessage id="Topbar.Topbar.sellLabel" />
                </NamedLink>
              )}
            </SellingAllowed>
            <NamedLink className={css.wishlistLink} name="WishlistPage">
              <span className={css.wishlistIconWrapper}>
                <FaRegHeart className={css.wishlistIcon} />
              </span>
            </NamedLink>
            <NamedLink
              className={css.inboxLink}
              name="InboxPage"
              params={{ tab: this.context && currentUserHasListings ? 'sales' : 'orders' }}
            >
              <span className={css.inbox}>
                <GoBell className={css.bell} />
                {notificationCount ? (
                  <span className={css.notificationBadge}>
                    {notificationCount > 0 ? notificationCount : null}
                  </span>
                ) : null}
              </span>
            </NamedLink>
            {/* {profileMenu} */}
            {currentUser ? (
              profileMenu
            ) : (
              <NamedLink name="ProfileSettingsPage" className={css.userIcon}>
                <img src={image} className={css.avatar} />
              </NamedLink>
            )}
          </div>
        </div>

        <div className={isMobileFilterOpen ? css.searchBarContainerMobile : css.searchBarContainer}>
          <div className={css.searchBar}>
            <TopbarSearchForm
              onSubmit={this.handleSubmit}
              initialValues={initialSearchFormValues}
              appConfig={config}
              history={history}
              routeConfiguration={routeConfiguration}
              searchParams={currentSearchParams}
              clearKeywordsAfterSubmit={isSearchPage}
            />
          </div>
        </div>
        <div className={css.desktop}>
          <TopbarDesktop
            className={desktopClassName}
            currentUserHasListings={currentUserHasListings}
            currentUser={currentUser}
            currentPage={currentPage}
            initialSearchFormValues={initialSearchFormValues}
            intl={intl}
            isAuthenticated={isAuthenticated}
            notificationCount={notificationCount}
            onLogout={this.handleLogout}
            onSearchSubmit={this.handleSubmit}
            appConfig={config}
            pathname={pathname}
            history={history}
            routeConfiguration={routeConfiguration}
            searchParams={currentSearchParams}
            clearKeywordsAfterSubmit={isSearchPage}
          />
          <MegaMenu lang={intl.locale} />
        </div>
        <Modal
          id="TopbarMobileMenu"
          containerClassName={css.modalContainer}
          scrollLayerClassName={css.mobileMenuScrollLayer}
          isOpen={isMobileMenuOpen}
          onClose={this.handleMobileMenuClose}
          isTopbar={true}
          usePortal
          onManageDisableScrolling={onManageDisableScrolling}
        >
          {authInProgress ? null : mobileMenu}
        </Modal>
        <Modal
          id="TopbarMobileSearch"
          containerClassName={css.modalContainerSearchForm}
          isOpen={isMobileSearchOpen}
          onClose={this.handleMobileSearchClose}
          usePortal
          onManageDisableScrolling={onManageDisableScrolling}
        >
          <div className={css.searchContainer}>
            <TopbarSearchForm
              onSubmit={this.handleSubmit}
              initialValues={initialSearchFormValues}
              isMobile
              appConfig={config}
              history={history}
              routeConfiguration={routeConfiguration}
              searchParams={currentSearchParams}
              clearKeywordsAfterSubmit={isSearchPage}
            />
            <p className={css.mobileHelp}>
              <FormattedMessage id="Topbar.mobileSearchHelp" />
            </p>
          </div>
        </Modal>
        <ModalMissingInformation
          id="MissingInformationReminder"
          history={history}
          containerClassName={css.missingInformationModal}
          currentUser={currentUser}
          currentUserHasListings={currentUserHasListings}
          currentUserHasOrders={currentUserHasOrders}
          location={location}
          onManageDisableScrolling={onManageDisableScrolling}
          onResendVerificationEmail={onResendVerificationEmail}
          sendVerificationEmailInProgress={sendVerificationEmailInProgress}
          sendVerificationEmailError={sendVerificationEmailError}
          emailVerifyPopupOpen={emailVerifyPopupOpen}
          onHandleClosePopUp={onHandleClosePopUp}
        />
        <GenericError show={showGenericError} />
      </div>
    );
  }
}

TopbarComponent.defaultProps = {
  className: null,
  rootClassName: null,
  desktopClassName: null,
  mobileRootClassName: null,
  mobileClassName: null,
  notificationCount: 0,
  currentUser: null,
  currentUserHasOrders: null,
  currentPage: null,
  sendVerificationEmailError: null,
  authScopes: [],
  currentSearchParams: {},
};

TopbarComponent.propTypes = {
  className: string,
  rootClassName: string,
  desktopClassName: string,
  mobileRootClassName: string,
  mobileClassName: string,
  isAuthenticated: bool.isRequired,
  authScopes: array,
  authInProgress: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentUserHasListings: bool.isRequired,
  currentUserHasOrders: bool,
  currentPage: string,
  notificationCount: number,
  onLogout: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  onResendVerificationEmail: func.isRequired,
  sendVerificationEmailInProgress: bool.isRequired,
  sendVerificationEmailError: propTypes.error,
  showGenericError: bool.isRequired,

  // These are passed from Page to keep Topbar rendering aware of location changes
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string.isRequired,
  }).isRequired,
  currentSearchParams: object,

  // from withViewport
  viewport: shape({
    width: number.isRequired,
    height: number.isRequired,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,

  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,
};

const EnhancedTopbar = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  return (
    <TopbarComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      {...props}
    />
  );
};

const Topbar = withViewport(EnhancedTopbar);
Topbar.displayName = 'Topbar';

export default Topbar;
